import { graphql, useStaticQuery } from 'gatsby';
import React, { FC } from 'react';

import { useGetPlayerOverviewPageLink } from '@/bits/links/useLink';
import {
  Card,
  CardBody,
  CardOptions,
  CardOptionsButton,
  ControlledModal,
  InlineIconButton,
} from '@/components';
import { useDataGrid } from '@/components/DataGrid';
import {
  EditIcon,
  NextIcon,
  PlusIcon,
  PreviousIcon,
  RefreshIcon,
} from '@/components/icons';
import { useTranslate } from '@/contexts';
import {
  PlayerDocumentUploadForm,
  ReviewDocumentForm,
  ReviewFundingDocumentForm,
} from '@/forms';
import {
  DocumentSearchOrder,
  DocumentStatus,
  DocumentType,
} from '@/globalTypes';
import { getDocumentStatusLabel, isFundingDocument } from '@/utils';
import { getDocumentTypeName } from '@/utils/document-types';
import formatDate from '@/utils/formatter/formatDate';
import usePlayerDocuments from './useDocuments';

const BLOCK_STATIC_QUERY = graphql`
  query SanityPlayerDocumentsBlockStaticQuery {
    sanityPlayerDocumentsBlock {
      title {
        ...SanityLocaleString
      }
    }
  }
`;

const DocumentsBlock: FC = () => {
  const { t } = useTranslate();

  const block =
    useStaticQuery<Queries.SanityPlayerDocumentsBlockStaticQueryQuery>(
      BLOCK_STATIC_QUERY,
    ).sanityPlayerDocumentsBlock;

  const getPlayerOverviewLink = useGetPlayerOverviewPageLink();

  const {
    documents,
    fetching,
    nextPage,
    previousPage,
    refresh,
    orderBy,
    desc,
    setOrderBy,
    defaultFilter,
    updateFilter,
  } = usePlayerDocuments();

  const { DataGrid, GlobalCardOptions } = useDataGrid({
    name: 'documents',
    data: documents,
    fetching,
    orderBy,
    setOrderBy,
    desc,
    defaultFilter,
    updateFilter,
    schema: (s) => [
      s.stringValue({
        field: 'id',
        title: 'Document ID',
      }),
      s.stringValue({
        field: 'uuid',
        title: 'Document UUID',
        hidden: true,
      }),
      s.stringValue({
        field: 'playerId',
        title: 'Player ID',
        getValue: ({ row }) => row.player?.id,
        linkTo: ({ row }) => getPlayerOverviewLink(`${row.player?.id}`) ?? '',
      }),
      s.dateTimeValue({
        field: 'uploadedAt',
        title: 'Uploaded At',
        fromFilterField: 'from',
        toFilterField: 'to',
        getValue: ({ row }) => formatDate(row.uploadedAt),
        orderBy: DocumentSearchOrder.uploadedAt,
      }),
      s.enumValue({
        field: 'status',
        title: 'Status',
        e: DocumentStatus,
        filterField: 'status',
        getValue: ({ row }) => getDocumentStatusLabel(row.status),
      }),
      s.initiatorValue({
        field: 'uploadedBy',
        title: 'Uploaded By',
      }),
      s.initiatorValue({
        field: 'verifiedBy',
        title: 'Verified By',
        orderBy: DocumentSearchOrder.verifiedAt,
      }),
      s.enumValue({
        field: 'documentType',
        title: 'Document Type',
        e: DocumentType,
        filterField: 'documentType',
        isMulti: true,
        getValue: ({ row }) =>
          row.documentType && getDocumentTypeName(row.documentType),
      }),
      s.stringValue({
        field: 'verificationTypes',
        title: 'Verification Types',
        getValue: ({ row }) => row.verificationTypes.join(', ') || '-',
      }),
      s.stringValue({
        field: 'expirationDate',
        title: 'Expiration Date',
        getValue: ({ row }) => formatDate(row.expirationDate, true),
        orderBy: DocumentSearchOrder.expirationDate,
      }),
      s.dateTimeValue({
        field: 'deletedAt',
        title: 'Deleted At',
        orderBy: DocumentSearchOrder.deletedAt,
      }),
      s.initiatorValue({
        field: 'deletedBy',
        title: 'Deleted By',
      }),
      s.actionValue({
        field: 'actions',
        title: 'Actions',
        getValue: ({ row }) => (
          <ControlledModal
            content={
              isFundingDocument(row.documentType) ? (
                <ReviewFundingDocumentForm document={row} playerId={row.id} />
              ) : (
                <ReviewDocumentForm document={row} />
              )
            }
          >
            <InlineIconButton>
              <EditIcon />
            </InlineIconButton>
          </ControlledModal>
        ),
      }),
    ],
  });

  if (!block) {
    return null;
  }

  return (
    <Card
      size="lg"
      title={t(block.title)}
      showOptionsAtBottom
      options={
        <CardOptions>
          <CardOptionsButton
            disabled={!previousPage}
            onClick={() => previousPage && previousPage()}
          >
            <PreviousIcon />
          </CardOptionsButton>
          <CardOptionsButton
            disabled={!nextPage}
            onClick={() => nextPage && nextPage()}
          >
            <NextIcon />
          </CardOptionsButton>
          <GlobalCardOptions />

          <CardOptionsButton
            onClick={() => refresh({ requestPolicy: 'network-only' })}
          >
            <RefreshIcon />
          </CardOptionsButton>
          <ControlledModal
            content={<PlayerDocumentUploadForm isGlobalPlayer={false} />}
          >
            <CardOptionsButton>
              <PlusIcon />
            </CardOptionsButton>
          </ControlledModal>
        </CardOptions>
      }
    >
      <CardBody>
        <DataGrid />
      </CardBody>
    </Card>
  );
};

export default DocumentsBlock;
